import { useTranslation } from 'react-i18next';

import type { PlatformUserRole } from '@nucleus-commerce/api-client/platform';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

export interface RoleSelectProps {
  value: PlatformUserRole;
  onChange: (role: PlatformUserRole) => void;
  /** Roles the actor may hand out; the current value is always shown even when not assignable. */
  roles: readonly PlatformUserRole[];
  disabled?: boolean;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

export function RoleSelect({ value, onChange, roles, disabled, ...aria }: RoleSelectProps) {
  const { t } = useTranslation(['users', 'common']);
  const options = roles.includes(value) ? roles : [value, ...roles];
  return (
    <Select
      value={value}
      onValueChange={(next) => onChange(next as PlatformUserRole)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full" aria-label={t('users:fields.role')} {...aria}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((role) => (
          <SelectItem key={role} value={role} className="capitalize">
            <span className="flex flex-col items-start gap-0.5">
              <span className="capitalize">{t(`common:roles.${role}`)}</span>
              <span className="text-muted-foreground text-xs normal-case">
                {t(`users:roleHelp.${role}`)}
              </span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
